import { createContext, useContext } from 'react';
import PropTypes from 'prop-types';

import { AppStore } from 'stores/AppStore';

import { contentfulApi } from './api/contentfulApi';

/**
 * CMS content entity names.
 * The CMS adapter module maps those into it's own id's, allowing to switch to
 * a different CMS provider without changing our code.
 *
 * Guidelines:
 * - Page entities should start with `PAGE_`.
 */
export const entities = {
  PAGE_HOMEPAGE: 'PAGE_HOMEPAGE',
  PAGE_FAQ: 'PAGE_FAQ',
  PAGE_PRIVACY_POLICY: 'PAGE_PRIVACY_POLICY',
  PAGE_COOKIE_NOTICE: 'PAGE_COOKIE_NOTICE',
  PAGE_TERMS_AND_CONDITIONS: 'PAGE_TERMS_AND_CONDITIONS',
  PAGE_ELIGIBILITY: 'PAGE_ELIGIBILITY',
  PAGE_DELIVERY_OPTIONS: 'PAGE_DELIVERY_OPTIONS',
};

const CmsContext = createContext();

/**
 * This React Context Provider is used to make it easier to get the CMS data
 * from anywhere in the components sub-tree.
 * Otherwise the data is only available in the page root component and would
 * have to be drilled down via `props`.
 */
export function CmsProvider({ children, content }) {
  return <CmsContext.Provider value={content}>{children}</CmsContext.Provider>;
}

CmsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  /**
   * Entities data map, e.g.:
   * {
   *   PAGE_HOMEPAGE: { heroTitle: '...', heroSubtitle: '...' }
   * }
   */
  content: PropTypes.object,
};

/**
 * React Hook to get a single entity from the CMS content via React Context.
 *
 * @param {string} entityName
 */
export function useCmsContent() {
  const content = useContext(CmsContext);

  return content;
}

async function getAllCategories() {
  return await contentfulApi.fetchCategories();
}

async function getArticle(articleSlug, subcategorySlug, categorySlug) {
  return await contentfulApi.fetchArticle(
    articleSlug,
    subcategorySlug,
    categorySlug
  );
}

async function getAllArticles(limit = 100) {
  return await contentfulApi.fetchAllArticles(limit);
}

async function getArticlePromosBySubcategory(subcategorySlug, articleSlug) {
  return await contentfulApi.fetchArticlePromosBySubcategory(
    subcategorySlug,
    articleSlug
  );
}

async function getLobbyCategoryPromos() {
  return await contentfulApi.fetchLobbyCategoryPromos();
}

async function getInfoCenterHeroPromos() {
  return await contentfulApi.fetchInfoCenterHeroPromos();
}

async function getLobbyNissaPromos() {
  return await contentfulApi.fetchLobbyNissaPromos();
}

async function getLobbyQAPromos() {
  return await contentfulApi.fetchLobbyQAPromos();
}

async function getInfoCenterLobbyContent() {
  const categoryPromos = await getLobbyCategoryPromos();
  const heroPromos = await getInfoCenterHeroPromos();
  const nissaPromos = await getLobbyNissaPromos();
  const qaPromos = await getLobbyQAPromos();

  return { categoryPromos, heroPromos, nissaPromos, qaPromos };
}

async function getDrNissaArticles(limit, skip) {
  return contentfulApi.fetchDrNissaArticles(limit, skip);
}

async function getQuestionArticles(limit, skip) {
  return contentfulApi.fetchQuestionArticles(limit, skip);
}

async function getCategory(categorySlug) {
  return await contentfulApi.fetchCategory(categorySlug);
}

async function getCategoryRichText(categorySlug) {
  return await contentfulApi.fetchCategoryRichText(categorySlug);
}

async function getCategoryContent(categorySlug) {
  const category = await getCategory(categorySlug);
  const infoCenterCategories = await getAllCategories();
  const richText = await getCategoryRichText(categorySlug);

  return {
    category,
    infoCenterCategories,
    richText,
  };
}

async function getSubcategoryPagePromos(subcategorySlug) {
  return await contentfulApi.fetchSubcategoryPagePromos(subcategorySlug);
}

async function getAllSubcategories() {
  return await contentfulApi.fetchAllSubcategories();
}

async function getSubcategory(subcategorySlug, categorySlug) {
  return await contentfulApi.fetchSubcategory(subcategorySlug, categorySlug);
}

async function getSubcategoryRichText(subcategorySlug) {
  return await contentfulApi.fetchSubcategoryRichText(subcategorySlug);
}

async function getEntry(entryName, filterBy) {
  return await contentfulApi.fetchEntry(
    entryName,
    AppStore.isDipUti ? 'dipUti' : filterBy
  );
}

async function getPageMetadata({ pageSlug, localeCode, isDipUti }) {
  return await contentfulApi.fetchPageMetadata({
    pageSlug,
    localeCode,
    isDipUti,
  });
}

async function getLimitedNotice(localeCode) {
  return await contentfulApi.fetchLimitedNotice(localeCode);
}

async function getInfoCenterTopMenu() {
  return await contentfulApi.fetchTopBarMenu();
}

async function getInfoCenterTileMenu() {
  return await contentfulApi.fetchTileMenu();
}

export const cms = {
  getAllCategories,
  getAllSubcategories,
  getArticle,
  getAllArticles,
  getArticlePromosBySubcategory,
  getInfoCenterLobbyContent,
  getDrNissaArticles,
  getQuestionArticles,
  getCategoryContent,
  getSubcategoryPagePromos,
  getSubcategory,
  getSubcategoryRichText,
  getEntry,
  getPageMetadata,
  getLimitedNotice,
  getInfoCenterTopMenu,
  getInfoCenterTileMenu,
};
